import React, { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Container, Row, Spinner } from "react-bootstrap";
import { ExternalLink, Home, MessageSquareText, ThumbsDown, ThumbsUp, UserRound } from "lucide-react";
import api from "Common/api";
import { toast, ToastContainer } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import "./recommended-properties.css";

type SharedProperty = {
  listingKey?: string;
  listingId?: string;
  thumbnailUrl?: string;
  propertyType?: string;
  propertySubType?: string;
  listPrice?: number;
  bedsTotal?: number;
  bathsTotal?: number;
  sqftTotal?: number;
  UnparsedAddress?: string;
  StreetAddress?: string;
  City?: string;
  State?: string;
  PostalCode?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  reaction?: "like" | "dislike" | null;
};

type SharedCollection = {
  id: number;
  name: string;
  note?: string;
  agentName?: string;
  createdAt: string;
  properties: SharedProperty[];
};

function formatPrice(value?: number) {
  if (!Number(value)) return "Price on request";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(value));
}

function address(property: SharedProperty) {
  return [
    property.UnparsedAddress || property.StreetAddress,
    property.City || property.city,
    property.State || property.state,
    property.PostalCode || property.postalCode,
  ].filter(Boolean).join(", ") || "Address available in property details";
}

function publicPropertyUrl(property: SharedProperty) {
  const id = property.listingId || property.listingKey;
  return `https://www.relibuild.com/property-detail/property-${encodeURIComponent(String(id || ""))}`;
}

export default function HomeownerRecommendedProperties() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [collections, setCollections] = useState<SharedCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reacting, setReacting] = useState<string | null>(null);
  const requestedCollectionId = Number(searchParams.get("collection"));
  const visibleCollections = Number.isInteger(requestedCollectionId) && requestedCollectionId > 0
    ? collections.filter((collection) => collection.id === requestedCollectionId)
    : collections;

  useEffect(() => {
    document.title = "Recommended Properties | RELiBUILD";
    api.get("/agent/property-collections/homeowner")
      .then((response: any) => {
        const data = response?.data?.data ?? response?.data;
        setCollections(Array.isArray(data) ? data : []);
      })
      .catch((requestError: any) => {
        setError(requestError?.response?.data?.message || "Unable to load your recommended properties.");
      })
      .finally(() => setLoading(false));
  }, []);

  const setPropertyReaction = async (collectionId: number, property: SharedProperty, requested: "like" | "dislike") => {
    const key = String(property.listingKey || property.listingId || "");
    if (!key || reacting) return;
    const previous = property.reaction || null;
    const next = previous === requested ? null : requested;
    const requestKey = `${collectionId}:${key}`;

    setReacting(requestKey);
    setCollections((current) => current.map((collection) => collection.id !== collectionId ? collection : {
      ...collection,
      properties: collection.properties.map((item) => String(item.listingKey || item.listingId || "") === key ? { ...item, reaction: next } : item),
    }));
    try {
      await api.put(`/agent/property-collections/${collectionId}/properties/${encodeURIComponent(key)}/reaction`, { reaction: next });
    } catch (requestError: any) {
      setCollections((current) => current.map((collection) => collection.id !== collectionId ? collection : {
        ...collection,
        properties: collection.properties.map((item) => String(item.listingKey || item.listingId || "") === key ? { ...item, reaction: previous } : item),
      }));
      toast.error(requestError?.response?.data?.message || "Unable to save your feedback.");
    } finally {
      setReacting(null);
    }
  };

  return (
    <div className="page-content homeowner-recommendations-page">
      <Container fluid>
        {visibleCollections.length !== collections.length && (
          <div className="recommendations-filter-notice">
            <span>Showing the selected property collection</span>
            <Button variant="outline-primary" size="sm" onClick={() => setSearchParams({})}>View all recommendations</Button>
          </div>
        )}

        {loading && <div className="recommendations-loading"><Spinner animation="border" /><span>Loading recommendations…</span></div>}
        {!loading && error && <div className="alert alert-warning">{error}</div>}
        {!loading && !error && collections.length === 0 && (
          <Card className="recommendations-empty"><Card.Body><Home size={46} /><h4>No recommendations yet</h4><p>Your agent’s shared property collections will appear here.</p></Card.Body></Card>
        )}

        {!loading && !error && collections.length > 0 && visibleCollections.length === 0 && (
          <div className="alert alert-warning">This recommended property collection is no longer available. <Button variant="link" className="p-0 align-baseline" onClick={() => setSearchParams({})}>View all recommendations</Button></div>
        )}

        {!loading && !error && visibleCollections.map((collection) => (
          <Card className="shared-property-collection" key={collection.id}>
            <Card.Header>
              <div>
                <div className="shared-by-agent"><UserRound size={16} /> Recommended by {collection.agentName || "your agent"}</div>
                <h4>{collection.name}</h4>
                {collection.note && <p><MessageSquareText size={17} /> {collection.note}</p>}
              </div>
              <small>Shared {new Date(collection.createdAt).toLocaleDateString()}</small>
            </Card.Header>
            <Card.Body>
              <Row className="g-4">
                {(collection.properties || []).map((property) => (
                  <Col xxl={3} xl={4} md={6} key={property.listingKey || property.listingId}>
                    <Card className="recommended-property-card h-100">
                      <div className="recommended-property-photo">
                        {property.thumbnailUrl ? <img src={property.thumbnailUrl} alt={address(property)} /> : <div><Home size={40} /></div>}
                        <Badge bg="light" text="dark">{property.propertySubType || property.propertyType || "Property"}</Badge>
                      </div>
                      <Card.Body>
                        <h4>{formatPrice(property.listPrice)}</h4>
                        <p>{address(property)}</p>
                        <div className="recommended-property-facts">
                          <span><b>{property.bedsTotal ?? "—"}</b> beds</span>
                          <span><b>{property.bathsTotal ?? "—"}</b> baths</span>
                          <span><b>{property.sqftTotal ? Number(property.sqftTotal).toLocaleString() : "—"}</b> sqft</span>
                        </div>
                        <div className="property-reaction-row">
                          <span>Interested?</span>
                          <div>
                            <button type="button" className={property.reaction === "like" ? "active like" : ""} aria-label="Like this property" aria-pressed={property.reaction === "like"} disabled={reacting === `${collection.id}:${property.listingKey || property.listingId}`} onClick={() => void setPropertyReaction(collection.id, property, "like")}><ThumbsUp size={17} /> Like</button>
                            <button type="button" className={property.reaction === "dislike" ? "active dislike" : ""} aria-label="Dislike this property" aria-pressed={property.reaction === "dislike"} disabled={reacting === `${collection.id}:${property.listingKey || property.listingId}`} onClick={() => void setPropertyReaction(collection.id, property, "dislike")}><ThumbsDown size={17} /> Dislike</button>
                          </div>
                        </div>
                        <Button as="a" href={publicPropertyUrl(property)} target="_blank" rel="noreferrer" className="w-100 mt-3">
                          View property <ExternalLink size={15} className="ms-1" />
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        ))}
      </Container>
      <ToastContainer position="top-right" />
    </div>
  );
}
